// Workspace-wide presence — knows which ticket each teammate is currently on.
//
// Lives a tier above `useTicketPresence`. Mounted once at the app shell so a
// single WebSocket connection covers all surfaces. Each teammate broadcasts
// `{slack_user_id, name, avatar_url, ticket_id | null}`. The dashboard
// reads this map to drop a small dot on rows where someone else is currently
// looking.
//
// Why a SECOND presence channel instead of reusing per-ticket ones:
//   - Per-ticket channels are cheap when one ticket is open, but joining a
//     channel per dashboard row is wasteful and would burst connection count.
//   - One workspace channel is one connection, regardless of dashboard size.
//
// Coexistence with `useTicketPresence`:
//   - Both channels run at the same time on the ticket-detail page. The
//     per-ticket channel renders the rich "viewing" pill in the header; the
//     workspace channel feeds the dashboard's small dot. They overlap by
//     design — different surfaces have different display needs.

import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export interface WorkspacePresenceEntry {
  slack_user_id: string;
  name: string;
  avatar_url: string | null;
  /** Currently-open ticket id, or null if the user is not on a ticket detail. */
  ticket_id: string | null;
}

interface Me {
  slack_user_id: string;
  name: string;
  avatar_url: string | null;
  email: string;
}

const WORKSPACE_CHANNEL = 'presence:workspace';

// Returns:
//   - A map: ticketId → array of OTHER team members currently on that ticket
//   - A setter: call when the local user navigates to/away from a ticket
//
// The map dedupes self out, so the dashboard's "someone else is here" dot
// never lights up because YOU happen to have the ticket open in another tab.
export function useWorkspacePresence(me: Me | null): {
  presenceByTicket: Map<string, WorkspacePresenceEntry[]>;
  setMyTicket: (ticketId: string | null) => void;
} {
  const [presenceByTicket, setPresenceByTicket] = useState<
    Map<string, WorkspacePresenceEntry[]>
  >(new Map());
  const [myTicket, setMyTicket] = useState<string | null>(null);

  useEffect(() => {
    if (!me?.slack_user_id) return;

    const channel = supabase.channel(WORKSPACE_CHANNEL, {
      config: { presence: { key: me.slack_user_id } },
    });

    const computeMap = () => {
      const state = channel.presenceState<WorkspacePresenceEntry>();
      const next = new Map<string, WorkspacePresenceEntry[]>();
      for (const key of Object.keys(state)) {
        if (key === me.slack_user_id) continue; // skip self
        const list = state[key] as WorkspacePresenceEntry[];
        const first = list?.[0];
        if (!first || !first.ticket_id) continue;
        const arr = next.get(first.ticket_id) ?? [];
        // Dedupe by slack_user_id within a ticket (multi-tab guard).
        if (!arr.some((x) => x.slack_user_id === first.slack_user_id)) {
          arr.push(first);
          next.set(first.ticket_id, arr);
        }
      }
      setPresenceByTicket(next);
    };

    channel
      .on('presence', { event: 'sync' }, computeMap)
      .on('presence', { event: 'join' }, computeMap)
      .on('presence', { event: 'leave' }, computeMap)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            slack_user_id: me.slack_user_id,
            name: me.name,
            avatar_url: me.avatar_url,
            ticket_id: myTicket,
          } as WorkspacePresenceEntry);
        }
      });

    return () => {
      void channel.untrack().finally(() => {
        void supabase.removeChannel(channel);
      });
      setPresenceByTicket(new Map());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.slack_user_id, me?.name, me?.avatar_url, me?.email]);

  // Update broadcast when the local user navigates between tickets.
  // We re-track on the same channel rather than tearing it down — Realtime
  // tracks the latest payload per presence key.
  useEffect(() => {
    if (!me?.slack_user_id) return;
    const ch = supabase.getChannels().find((c) => c.topic === `realtime:${WORKSPACE_CHANNEL}`);
    if (!ch) return;
    void ch.track({
      slack_user_id: me.slack_user_id,
      name: me.name,
      avatar_url: me.avatar_url,
      ticket_id: myTicket,
    } as WorkspacePresenceEntry);
  }, [myTicket, me?.slack_user_id, me?.name, me?.avatar_url]);

  return { presenceByTicket, setMyTicket };
}
